import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Flower, CheckCircle, LogOut, Lock, Sparks } from 'iconoir-react';
import { toast } from 'sonner';
import { DEFAULT_PRICE_OBJECT } from '../constants/pricing';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FEATURES = [
  "Culturally-specific wedding templates (Punjabi Sikh, North/South Indian, Pakistani, Bengali, Gujarati)",
  "Multi-event budget tracking (Mehndi, Sangeet, Haldi, Ceremony, Reception & more)",
  "Live multi-currency conversion across 10 currencies",
  "Seating chart with drag-and-drop, table diagrams & printable layouts",
  "Event timeline builder with PDF export",
  "Guest list & RSVP tracking, photo uploads & collaborator access",
  "Spending benchmarks vs South Asian wedding averages",
  "Payment reminders, countdowns & vendor tracking",
];

export default function PaywallPage() {
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [price, setPrice] = useState(DEFAULT_PRICE_OBJECT);

  useEffect(() => {
    // If user is already paid, skip paywall
    if (user && user.is_paid) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    axios.get(`${API}/payments/price`).then((r) => setPrice(r.data)).catch(() => {});
  }, []);

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/payments/checkout`,
        { origin_url: window.location.origin },
        { withCredentials: true }
      );
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error('Could not start checkout');
        setLoading(false);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Checkout failed. Please try again.');
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  // Refresh auth state when coming back to paywall
  useEffect(() => {
    if (checkAuth) checkAuth();
  }, []); // eslint-disable-line

  return (
    <div
      className="min-h-screen relative overflow-hidden"
      data-testid="paywall-page"
      style={{ background: '#FFFFFF' }}
    >
      {/* Decorative blurred orbs — warm + green */}
      <div className="absolute top-32 -left-20 w-72 h-72 rounded-full bg-terracotta/15 blur-3xl pointer-events-none hidden" />
      <div className="absolute top-1/2 -right-20 w-80 h-80 rounded-full bg-ochre/20 blur-3xl pointer-events-none hidden" />
      <div className="absolute bottom-20 left-1/3 w-96 h-96 rounded-full bg-moss/10 blur-3xl pointer-events-none hidden" />
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-10 lg:py-16 relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-12">
          <div className="flex items-center gap-3">
            <Flower className="w-8 h-8 text-terracotta" strokeWidth={1.5} />
            <span className="font-brand text-2xl text-charcoal">Shaadi Planner</span>
          </div>
          <button
            onClick={handleLogout}
            className="text-sm text-muted hover:text-charcoal flex items-center gap-1.5 transition-colors"
            data-testid="paywall-logout-btn"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} />
            Sign out
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Left: Pitch */}
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-terracotta/10 text-terracotta rounded-full text-xs font-medium mb-6">
              <Lock className="w-3 h-3" strokeWidth={2} />
              One-time payment · Lifetime access
            </div>
            <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-charcoal tracking-tight leading-tight mb-6">
              Plan every event of your South Asian wedding — beautifully.
            </h1>
            <p className="text-muted font-light text-lg mb-10 leading-relaxed">
              From Mehndi to Reception, Shaadi Planner gives you everything you need: event-specific budgets, seating charts, timelines, RSVPs, and spend benchmarks tailored to South Asian weddings.
            </p>

            <div className="space-y-3">
              {FEATURES.map((f) => (
                <div key={f} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-moss flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                  <span className="text-charcoal text-sm lg:text-base">{f}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Price + CTA */}
          <div className="lg:sticky lg:top-8">
            <div
              className="relative bg-page rounded-3xl p-8 lg:p-10 border border-terracotta/20 overflow-hidden"
              style={{ boxShadow: '0 30px 80px -20px rgba(196, 87, 53, 0.25), 0 0 0 1px rgba(196, 87, 53, 0.06)' }}
              data-testid="paywall-checkout-card"
            >
              {/* Decorative ornaments */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-1 bg-gradient-to-r from-transparent via-terracotta to-transparent" />
              <div className="absolute top-5 right-5 text-terracotta/30 text-3xl font-serif">✦</div>
              <div className="absolute bottom-5 left-5 text-ochre/30 text-2xl font-serif">❋</div>
              {price.is_founder ? (
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-ochre/15 text-ochre rounded-full text-[10px] tracking-[0.2em] uppercase font-mono mb-4" data-testid="paywall-founder-badge">
                  <Sparks className="w-3 h-3" strokeWidth={2} />
                  Founder's — Early users locking in lower rates
                </div>
              ) : (
                <p className="text-xs uppercase tracking-widest text-muted mb-3">Shaadi Planner — Lifetime</p>
              )}
              <div className="flex items-baseline gap-3 mb-1">
                <span className="font-serif font-light text-6xl text-charcoal" data-testid="paywall-price">
                  ${price.amount}
                </span>
                <span className="text-muted text-sm">{price.currency}</span>
              </div>
              {price.is_founder && price.previous_price && (
                <p className="text-muted/80 text-xs mt-2 font-light" data-testid="paywall-social-proof">
                  Previously: <span className="line-through">${price.previous_price}</span>
                  <span className="ml-1 text-muted/60">(First {price.slots_total ?? 100} spots sold out)</span>
                </p>
              )}
              <p className="text-sm text-muted mb-6 mt-2">
                One-time payment · Locked in for life · No subscriptions
              </p>

              {/* Scarcity bar + claimed copy */}
              {price.is_founder && price.slots_total && (
                <div className="mb-3">
                  <div className="flex items-center justify-between text-[10px] font-mono tracking-widest uppercase text-muted mb-2">
                    <span>This tier</span>
                    <span className="text-terracotta">
                      {price.slots_taken ?? 0} of {price.slots_total} claimed
                    </span>
                  </div>
                  <div className="h-1 bg-page rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-ochre to-terracotta transition-all duration-500"
                      style={{ width: `${Math.min(100, ((price.slots_taken ?? 0) / price.slots_total) * 100)}%` }}
                    />
                  </div>
                </div>
              )}
              {price.is_founder && price.next_price && (
                <p className="text-xs text-muted font-light mb-6" data-testid="paywall-urgency">
                  The next {Math.max(0, (price.slots_total ?? 0) - (price.slots_taken ?? 0))} users unlock this price. Then it increases to <span className="font-mono text-charcoal">${price.next_price}</span>.
                </p>
              )}

              <Button
                onClick={handleCheckout}
                disabled={loading}
                className="btn-primary w-full py-6 text-base"
                data-testid="paywall-checkout-btn"
              >
                {loading
                  ? 'Redirecting to checkout…'
                  : price.is_founder
                    ? `Lock In $${price.amount} Before It Increases`
                    : `Unlock Shaadi Planner — $${price.amount}`}
              </Button>

              <p className="text-xs text-muted text-center mt-4 font-light">
                One-time payment · Lifetime access · No subscriptions
              </p>
              <p className="text-[10px] text-muted/70 text-center mt-2 font-mono tracking-wider uppercase">
                Secure checkout · Stripe · 7-day refund
              </p>

              {user && (
                <div className="mt-6 pt-6 border-t border-border">
                  <p className="text-xs text-muted">
                    Signed in as <span className="text-charcoal font-medium">{user.email}</span>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
